/**
 * Single writer of the bearer token the API client sends.
 *
 * `apiClient` (services/api-services.ts) reads `sessionStorage.sessionId` in
 * its request interceptor and sets `Authorization: Bearer <token>` from it.
 * `SessionTokenSync` keeps that key in step with the Supabase session for
 * every page under the `(consent-manger)` layout — but routes OUTSIDE that
 * layout (`/auth/accept-terms`, and the signup form right after `signUp()`
 * returns a session) never had the token written, so their API calls went out
 * unauthenticated and the backend answered 401 (#7833 gap 2: the čl. 28
 * consent interstitial failed open on that 401 and forwarded the user through
 * without recording an acceptance — 0 `dpa_acceptances` rows across every
 * OAuth/email signup since the terms went live).
 *
 * Callers that hold a session outside the provider tree call this before
 * their first API request. Idempotent; a missing session writes nothing.
 */

import { createClient } from '@/utils/supabase/client';

export const API_SESSION_TOKEN_KEY = 'sessionId';

/** Minimal shape — accepts a Supabase `Session` without importing its type. */
interface ApiSessionTokenSource {
  access_token?: string | null;
}

/**
 * Write the session's access token where `apiClient` reads it.
 *
 * @returns `true` when a token was written, `false` when there was no session
 *   / token to write or `sessionStorage` is unavailable (SSR, private mode,
 *   blocked storage) — the caller decides whether that is fail-open.
 */
export function syncApiSessionToken(
  session: ApiSessionTokenSource | null | undefined
): boolean {
  const token = session?.access_token;
  if (!token) return false;
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return false;
    window.sessionStorage.setItem(API_SESSION_TOKEN_KEY, token);
    return true;
  } catch {
    // sessionStorage can throw (disabled storage, quota) — never break auth.
    return false;
  }
}

/** Shape of `supabase.auth.getSession()`'s resolved value that we rely on. */
type ApiSessionGetter = () => Promise<{
  data: { session: ApiSessionTokenSource | null };
}>;

const defaultSessionGetter: ApiSessionGetter = () =>
  createClient().auth.getSession();

/**
 * Read the current Supabase browser session and sync its access token for
 * `apiClient`. For call sites outside the `SupabaseAuthProvider` tree that are
 * about to make an authenticated API call (the čl. 28 consent interstitial).
 *
 * `getSession` is injectable so the util is testable without process-global
 * module mocks; production callers use the default.
 *
 * @returns whether a token was written — see `syncApiSessionToken`.
 */
export async function syncApiSessionTokenFromSupabase(
  getSession: ApiSessionGetter = defaultSessionGetter
): Promise<boolean> {
  const { data } = await getSession();
  return syncApiSessionToken(data.session);
}
